import { Component, Inject, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { IntegrationType, Recipe, RecipeTemplate } from '@pdqdev/pdq-shared/dist/entities';
import { DynamicRecipeModels } from '@pdqdev/pdq-shared/dist/interfaces';
import { finalize } from 'rxjs/operators';
import { IntegrationsService } from '@services/integrations/integrations.service';
import { ToastService } from '@storybook/services/toast/toast.service';
export interface VariableWithValues extends DynamicRecipeModels.Variable {
  isValid?: boolean;
}
@Component({
  selector: 'recipe-editor',
  templateUrl: './recipe-editor.component.html',
  styleUrls: ['./recipe-editor.component.scss'],
  standalone: false,
})
export class RecipeEditorComponent implements OnInit, OnChanges {
  variablesState: { [key: string]: VariableWithValues } = {};
  recipeTemplate: RecipeTemplate;
  recipeElements: string[] = [];
  InputType = DynamicRecipeModels.InputType;
  isLoading: boolean;
  dirty = false;
  isEdit: boolean;
  constructor(
    private toastService: ToastService,
    private integrationsService: IntegrationsService,
    public dialogRef: MatDialogRef<RecipeEditorComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      recipeTemplate: RecipeTemplate;
      recipe?: Recipe;
      integrationType: IntegrationType;
    },
  ) {}

  ngOnInit(): void {
    this.recipeTemplate = this.data?.recipeTemplate;
    this.initIsEdit();
    this.initVariablesState();
    this.initRecipeElements();
  }
  ngOnChanges(changes: SimpleChanges) {
    if (changes.data) {
      this.recipeTemplate = this.data?.recipeTemplate;
      this.initIsEdit();
    }
  }

  private initRecipeElements() {
    if (this.recipeTemplate) {
      this.recipeElements = this.recipeTemplate.description.split(' ');
    }
  }
  private initIsEdit() {
    this.isEdit = !!this.data?.recipe?.id;
  }
  private initVariablesState() {
    if (this.recipeTemplate) {
      const variablesConfig = this.data?.recipe?.metadata?.variables
        ? ((Object.values(this.data?.recipe?.metadata?.variables) as DynamicRecipeModels.Variable[]) ?? [])
        : (this.recipeTemplate.dynamicPartsConfig?.variables as DynamicRecipeModels.Variable[]);

      variablesConfig.forEach((variable) => {
        this.variablesState[variable.selector] = { ...variable, inputConfig: { ...variable.inputConfig } };
      });
    }
  }
  isVariable(element: string): boolean {
    const indexOfOpener = element.indexOf('${');
    const indexOfCloser = element.indexOf('}');
    return indexOfOpener !== -1 && indexOfCloser !== -1 && indexOfCloser > indexOfOpener;
  }
  handleVariableChange(variable: VariableWithValues) {
    this.variablesState[variable.selector] = { ...variable, inputConfig: { ...variable.inputConfig } };
    this.dirty = true;
  }
  getVariable(element: string) {
    if (!this.isVariable(element)) {
      return '';
    }
    const indexOfOpener = element.indexOf('${');
    const indexOfCloser = element.indexOf('}');
    const selector = element.substring(indexOfOpener + 2, indexOfCloser);
    return this.variablesState[selector];
  }
  onClose() {
    this.dialogRef.close();
  }
  private createToastOptions(isError: boolean) {
    if (isError) {
      return { message: `Failed to ${this.isEdit ? 'update' : 'add'} recipe`, mode: 'danger' };
    } else {
      return { message: `Recipe ${this.isEdit ? 'updated' : 'added'}`, mode: 'success' };
    }
  }

  get variablesValid() {
    return Object.values(this.variablesState).every((variable) => !!variable.isValid);
  }

  onAdd() {
    const recipe = this.data?.recipe ? this.data?.recipe : new Recipe();
    recipe.template = this.data.recipeTemplate;
    recipe.metadata = recipe.metadata ? { ...recipe.metadata } : {};
    recipe.metadata.variables = this.variablesState;
    this.isLoading = true;
    // If new recipe, then is active true
    recipe.isActive = recipe.isActive ?? true;
    this.integrationsService
      .postRecipe(recipe, this.data?.integrationType)
      .pipe(
        finalize(() => {
          this.isLoading = false;
          this.onClose();
        }),
      )
      .subscribe(
        (updatedRecipe) => {
          this.toastService.showToast(this.createToastOptions(false));
        },
        (err) => {
          console.log(err);
          this.toastService.showToast(this.createToastOptions(true));
        },
      );
  }

  isIntegrationName(element: string): boolean {
    const displayName = this.data?.integrationType?.displayName;
    return !!displayName && element.toLowerCase() === displayName.toLowerCase();
  }
}
